"use client";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  LayoutDashboard,
  Star,
  Stethoscope,
  MapPin,
  CreditCard,
  CalendarCheck,
} from "lucide-react";

interface HeroProps {
  isAdmin: boolean;
  onBookingClick: () => void;
}

const TRUST_BADGES = [
  { icon: Star, label: "2200+ Happy Patients" },
  { icon: Star, label: "5-Star Google Rating" },
  { icon: Stethoscope, label: "Doctor-led Care" },
  { icon: MapPin, label: "Vadodara" },
  { icon: CreditCard, label: "Transparent Pricing" },
  { icon: CalendarCheck, label: "Appointments Only" },
];

export function Hero({ isAdmin, onBookingClick }: HeroProps) {
  return (
    <section
      id="hero"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      aria-label="Hero Section"
    >
      {/* Background Image — lighter overlay so image shows clearly */}
      <div className="absolute inset-0 z-0">
        <Image
          src="/Hero.webp"
          alt="SkinSio Clinics Vadodara — Expert Medical Aesthetic Clinic"
          fill
          className="object-cover"
          priority
          fetchPriority="high"
          sizes="100vw"
          quality={85}
        />
        {/* Single continuous overlay — image visible near the top, fully
            fading to the page background by the bottom. Using one gradient
            (instead of two stacked ones) avoids a visible seam line. */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/50 to-background" />
      </div>

      {/* Decorative color blobs — kept lower and to the sides so they add
          color to the page without tinting the hero photo itself */}
      <div className="pointer-events-none absolute top-1/3 -right-16 z-0 h-72 w-72 rounded-full bg-golden/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 z-0 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 pt-24 text-center sm:pt-28 lg:pt-6">
        <div className="mx-auto max-w-3xl">
          {/* Admin Shortcut */}
          {isAdmin && (
            <div className="animate-fade-in-up mb-6 opacity-0">
              <Button
                variant="outline"
                size="sm"
                asChild
                className="mx-auto w-fit md:flex"
              >
                <a
                  href="/dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Go to Dashboard"
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Dashboard
                </a>
              </Button>
            </div>
          )}

          {/* Trust Badges — sit in normal flow above the eyebrow pill, so
              they can never overlap other hero content on any screen size.
              They wrap naturally on narrow viewports. */}
          <div className="mb-5 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
            {TRUST_BADGES.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-1.5 rounded-full border border-white/40 bg-white/85 px-3 py-1.5 text-xs font-semibold text-primary shadow-sm backdrop-blur-md sm:text-sm"
              >
                <Icon className="h-3.5 w-3.5 text-golden sm:h-4 sm:w-4" aria-hidden="true" />
                {label}
              </div>
            ))}
          </div>

          <h1 className="animate-fade-in-up animation-delay-100 mb-6 opacity-0">
  <span className="block text-4xl font-bold tracking-tight text-[#cc8c23] md:text-[40px]">
    Expert Medical Aesthetic Clinics
  </span>

  <span className="mt-3 block text-xl font-medium leading-snug text-primary md:text-2xl">
    Personalised Aesthetic Medicine. Real Results.
  </span>
</h1>
          <p className="text-muted-foreground animate-fade-in-up animation-delay-200 mx-auto mb-8 max-w-lg text-lg opacity-0 md:text-xl">
            Evidence-based Skin & Hair Treatments by Expert Doctors in
            state-of-the-art facility.
          </p>

          {/* CTA Buttons */}
          <div className="animate-fade-in-up animation-delay-300 mx-auto flex flex-col items-center justify-center gap-4 text-center opacity-0 sm:flex-row">
            <Button
              size="lg"
              onClick={onBookingClick}
              className="w-fit min-w-[224px] bg-gradient-to-r from-rose to-golden px-8 text-lg text-white shadow-lg shadow-rose/20 hover:opacity-90"
              aria-label="Book an appointment now"
            >
              Book Appointment
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-primary/30 hover:border-primary w-fit min-w-[224px] text-lg"
            >
              <a href="#services" aria-label="View our services">
                Our Services
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"
        aria-hidden="true"
      >
        <div className="border-foreground/30 flex h-10 w-6 justify-center rounded-full border-2 pt-2">
          <div className="bg-golden h-3 w-1 rounded-full" />
        </div>
      </div>
    </section>
  );
}
